import React, { useMemo } from 'react';
import { Paper, Typography, Box, Skeleton, useTheme } from '@mui/material';
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip,
    Legend,
} from 'recharts';
import { useTranslation } from 'react-i18next';

import { useGetReportOrderStatusBreakdown } from '../../hooks/reports/useGetReportOrderStatusBreakdown';
import { ORDER_STATUS_META } from '../orders/Table/OrderStatusBadge';
import useCurrency from '../../hooks/common/useCurrency';
import { formatNumber } from '../../utils/money';

const STATUS_COLOR_KEY = {
    pending: 'warning',
    confirmed: 'info',
    processing: 'info',
    shipped: 'primary',
    delivered: 'success',
    cancelled: 'grey',
    refunded: 'success',
    partially_refunded: 'warning',
    failed: 'error',
};

const OrderStatusBreakdown = ({ filter }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const theme = useTheme();
    const { format: formatPrice, locale } = useCurrency();

    const { items, isLoading } = useGetReportOrderStatusBreakdown({
        from: filter.from,
        to: filter.to,
    });

    const data = useMemo(
        () =>
            items.map((row) => {
                const status = row.status;
                const meta = ORDER_STATUS_META[status];
                const colorKey = STATUS_COLOR_KEY[status] || 'grey';
                const palette = theme.palette[colorKey];
                const color = palette?.main || theme.palette.primary.main;
                return {
                    status,
                    name: meta ? (isArabic ? meta.labelAr : meta.labelEn) : status,
                    count: Number(row.count) || 0,
                    revenue: Number(row.revenue) || 0,
                    color,
                };
            }),
        [items, isArabic, theme]
    );

    const total = data.reduce((s, d) => s + d.count, 0);
    const isEmpty = !isLoading && total === 0;

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                height: '100%',
            }}
        >
            <Typography variant="h6" fontWeight={700} mb={2}>
                {isArabic ? 'توزيع حالات الطلبات' : 'Order status breakdown'}
            </Typography>

            {isLoading ? (
                <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
            ) : isEmpty ? (
                <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">
                        {isArabic ? 'لا توجد بيانات' : 'No data'}
                    </Typography>
                </Box>
            ) : (
                <>
                    <Box sx={{ width: '100%', height: 280 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={data}
                                    dataKey="count"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={95}
                                    paddingAngle={2}
                                >
                                    {data.map((entry) => (
                                        <Cell key={entry.status} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: theme.palette.background.paper,
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 8,
                                    }}
                                    formatter={(value, name, props) => [
                                        `${formatNumber(value, locale)} (${formatPrice(props.payload.revenue || 0)})`,
                                        name,
                                    ]}
                                />
                                <Legend
                                    verticalAlign="bottom"
                                    height={36}
                                    wrapperStyle={{ fontSize: 12 }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                        {isArabic ? 'الإجمالي: ' : 'Total: '}
                        {formatNumber(total, locale)} {isArabic ? 'طلب' : 'orders'}
                    </Typography>
                </>
            )}
        </Paper>
    );
};

export default OrderStatusBreakdown;
